import { useState, useEffect } from 'react';
import { pb } from '@/lib/pocketbase';
import { Badge } from '@/components/ui/badge';
import { Bell } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';

interface LiveChatNotificationBadgeProps {
  className?: string;
}

const LiveChatNotificationBadge = ({ className = "" }: LiveChatNotificationBadgeProps) => {
  const { user } = useAuth();
  const [pendingCount, setPendingCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isActive = true;
    let pollInterval: NodeJS.Timeout | null = null;

    const fetchPendingChats = async () => {
      if (!user || user.role !== 'tutor') {
        setIsLoading(false);
        return;
      }

      try {
        // Check if user is a live chat agent first
        const agents = await pb.collection('liveChatAgents').getList(1, 1, {
          filter: `user = "${user.id}" && isActive = true`,
        });

        if (agents.items.length === 0) {
          setPendingCount(0);
          setIsLoading(false);
          return;
        }

        const agent = agents.items[0];

        // Only show notifications if agent is online
        if (agent.status !== 'online') {
          setPendingCount(0);
          setIsLoading(false);
          return;
        }

        // Get pending sessions (waiting or assigned to this agent)
        const sessions = await pb.collection('liveChatSessions').getList(1, 50, {
          filter: `(status = "waiting" || status = "assigned") && status != "closed"`,
          requestKey: `pending-chats-${Date.now()}`
        });

        if (isActive) {
          setPendingCount(sessions.items.length);
          setIsLoading(false);
        }
      } catch (error) {
        if (isActive) {
          console.error('Error fetching pending chats:', error);
          setPendingCount(0);
          setIsLoading(false);
        }
      }
    };

    // Initial fetch
    fetchPendingChats();

    // Set up polling every 3 seconds
    pollInterval = setInterval(fetchPendingChats, 3000);

    return () => {
      isActive = false;
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [user]);

  if (isLoading || pendingCount === 0) {
    return null;
  }

  return (
    <div className={`relative ${className}`}>
      <Badge 
        variant="destructive" 
        className="absolute -top-2 -right-2 h-5 w-5 p-0 flex items-center justify-center text-xs font-bold animate-pulse"
      >
        {pendingCount > 9 ? '9+' : pendingCount}
      </Badge>
      <Bell className="h-3 w-3 text-red-500 absolute -top-1 -right-1 animate-pulse" />
    </div>
  );
};

export default LiveChatNotificationBadge;